







































































import {
  defineComponent,
  nextTick,
  ref,
  useFetch,
  computed,
  useMeta,
  useRoute,
  onMounted,
  toRef,
  useContext, watch, onUnmounted,
} from '@nuxtjs/composition-api';
import { useStoryblokBridge } from '@storyblok/nuxt-2';
import CategoryPagination from '~/modules/catalog/category/components/pagination/CategoryPagination.vue';
import type { Pagination } from '~/composables/types';
import { addBasePath } from '~/helpers/addBasePath';
import { useUiHelpers } from '~/composables';
import {
  getMetaInfo,
  getStoryMeta,
  getUrlMeta,
  getPaginationMeta,
} from '~/helpers/getMetaInfo';

import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import { useUser } from '~/modules/customer/composables/useUser';
import useStoryblokView from '~/modules/storyblok/composables/useStoryblokView';
import BlogNewsArticleTile from '~/modules/storyblok/components/Blog/BlogNewsArticleTile.vue';
import useBlogCategories from '../../composables/useBlogCategories';

export default defineComponent({
  name: 'BlogCategory',
  components: {
    BlogNewsArticleTile,
    CategoryPagination,
    SkeletonLoader,
  },
  layout: 'basic',
  props: {
    currentStory: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const currentStory = toRef(props, 'currentStory');
    const route = useRoute();
    const { search } = route.value.query;
    const uiHelpers = useUiHelpers();
    const categoryUUID = currentStory.value?.uuid;
    const categoryType = currentStory.value?.content?.component;
    const {
      mainCategories,
      categoryTree,
      load: loadCategories,
      setCategoriesFullSlug,
    } = useBlogCategories();
    const { getArticleByCategoryUUID, getArticleBySubCategoryUUID } = useStoryblokView();
    const { addTags } = useCache();
    const { isAuthenticated } = useUser();
    const { app } = useContext();
    const lang = computed(() => app.i18n.locale);
    const pagination = ref<Pagination>({
      currentPage: 0,
      totalPages: 0,
    });
    const queryPage = () => Number(uiHelpers.resolveQuery()?.page ?? 1);
    const curentPage = () => (pagination.value.totalPages !== 0
      && queryPage() > pagination.value.totalPages
      ? 1
      : queryPage());

    const metaInfo = () => {
      const urlMeta = getUrlMeta(uiHelpers.resolvePath());
      const paginationMeta = getPaginationMeta({
        pagination: pagination.value,
        route: queryPage(),
        baseData: urlMeta,
      });
      return getStoryMeta({
        data: currentStory.value,
        baseData: paginationMeta,
        lang: lang.value,
      });
    };
    useMeta(() => getMetaInfo({ page: metaInfo() }));

    const getTotalPages = (headers) => Math.ceil(Number(headers.total) / Number(headers['per-page']));

    const articles = ref([]);
    const loading = ref(true);

    const currentMainCategory = computed(() => mainCategories.value.find((category) => currentStory.value?.full_slug.includes(category.full_slug)));
    const currentMainCategoryFullSlug = computed(
      () => currentMainCategory.value?.full_slug ?? '',
    );

    const { fetch } = useFetch(async () => {
      try {
        if (!categoryUUID) throw new Error('categoryUUID not provided');

        const getArticleAttrs = {
          uuid: categoryUUID,
          params: {
            search_term: (Array.isArray(search) ? search[0] : search) ?? '',
            per_page: 8,
            page: curentPage(),
            excluding_fields:
              'SEO,author,block,heroBackgraund,readMore,rich_text,subCategories,mainCategories',
            sort_by: 'content.wpPublishDate:desc',
          },
        };

        const { stories, headers } = categoryType === 'blogCategory'
          ? await getArticleByCategoryUUID(getArticleAttrs)
          : await getArticleBySubCategoryUUID(getArticleAttrs);

        pagination.value.currentPage = curentPage();
        pagination.value.totalPages = getTotalPages(headers);
        articles.value = stories;
        loading.value = false;
        await loadCategories();
      } catch (error) {
        console.log('error', error);
      } finally {
        loading.value = false;
      }
      if (!isAuthenticated.value) {
        addTags([
          { prefix: CacheTagPrefix.View, value: currentStory.value.full_slug },
        ]);
      }
    });
    fetch();

    watch(
      () => currentMainCategoryFullSlug.value,
      (newValue) => {
        setCategoriesFullSlug(newValue);
      },
    );

    onMounted(() => {
      if (currentStory.value?.id) {
        useStoryblokBridge(
          currentStory.value.id,
          (evStory) => {
            currentStory.value = evStory;
          },
          { preventClicks: true },
        );
      }
    });

    onUnmounted(() => {
      setCategoriesFullSlug('');
    });

    const postsContainerElement = ref<HTMLElement | null>(null);

    const goToPage = (page: number) => {
      uiHelpers.changePage(page, false);
      fetch();

      nextTick(() => {
        postsContainerElement.value?.scrollIntoView({ block: 'start' });
        window.scrollBy(0, -200);
      });
    };

    return {
      loading,
      mainCategories,
      categoryTree,
      currentMainCategoryFullSlug,
      queryPage,
      curentPage,
      goToPage,
      addBasePath,
      pagination,
      articles,
      postsContainerElement,
    };
  },
  head: {},
});
